/** Map offer/conversation API shapes to legacy bid UI fields (safe, shallow copies only). */

const MESSAGE_TYPE_MAP = {
  OFFER_CREATED: 'BID_PROPOSAL',
  OFFER_COUNTER: 'COUNTER_OFFER',
  OFFER_ACCEPTED: 'BID_ACCEPTED',
  OFFER_REJECTED: 'BID_REJECTED',
  OFFER_CANCELLED: 'BID_REJECTED',
  OFFER_EXPIRED: 'BID_REJECTED',
};

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Shallow product fields for list cards — avoids copying deep populated graphs. */
export const normalizeProductSnapshot = (product) => {
  if (!product) return null;
  if (typeof product === 'string') {
    const id = String(product).trim();
    return id ? { _id: id, id } : null;
  }
  if (!isPlainObject(product)) return null;
  const id = product._id || product.id;
  if (!id) return null;
  return {
    _id: String(product._id || product.id),
    id: String(product.id || product._id),
    name: product.name || product.title || '',
    title: product.title || product.name || '',
    images: product.images,
    image: product.image,
    price: product.price,
    newPrice: product.newPrice,
    computedPrice: product.computedPrice,
  };
};

const normalizeConversationRef = (conversation) => {
  if (!conversation) return null;
  if (typeof conversation === 'string') return conversation;
  if (isPlainObject(conversation)) {
    const id = conversation._id || conversation.id;
    return id ? String(id) : null;
  }
  return null;
};

const normalizeSellerSnapshot = (seller) => {
  if (!seller) return null;
  if (typeof seller === 'string') return { _id: seller, id: seller, name: '' };
  if (!isPlainObject(seller)) return null;
  const id = seller._id || seller.id;
  return {
    _id: id ? String(id) : '',
    id: id ? String(id) : '',
    name: seller.name || seller.storeName || '',
  };
};

/**
 * Normalizes an offer for list/detail UI while preserving product metadata
 * (name, image, dates) needed by My Bids and similar screens.
 */
export const normalizeOffer = (offer) => {
  if (!isPlainObject(offer)) return null;
  const id = offer.id || offer._id;
  if (!id) return null;
  const amount = offer.amount ?? offer.bidPrice ?? offer.bidAmount;
  const product = normalizeProductSnapshot(offer.product);
  const productId =
    product?.id ||
    (typeof offer.product === 'string' ? String(offer.product) : null);

  return {
    id: String(id),
    _id: String(offer._id || id),
    amount: Number(amount) || 0,
    bidPrice: Number(amount) || 0,
    bidAmount: Number(amount) || 0,
    status: offer.status,
    expiresAt: offer.expiresAt,
    createdAt: offer.createdAt,
    updatedAt: offer.updatedAt,
    convertedToCart: offer.convertedToCart ?? offer.status === 'CONVERTED',
    quantity: offer.quantity ?? 1,
    currency: offer.currency || 'USD',
    initiatedBy: offer.initiatedBy || 'BUYER',
    finalPrice: offer.finalPrice,
    product,
    productId,
    conversation: normalizeConversationRef(offer.conversation),
    seller: normalizeSellerSnapshot(offer.seller),
  };
};

export const isCounterMessage = (msg) =>
  msg?.type === 'COUNTER_OFFER' || msg?.type === 'OFFER_COUNTER';

export const isSellerCounterPending = (msg) => {
  const offer = getOfferFromMessage(msg);
  return (
    isCounterMessage(msg) &&
    offer?.status === 'PENDING' &&
    (offer?.initiatedBy === 'SELLER' || offer?.initiatedBy === 'seller')
  );
};

export const getSenderId = (msg) => {
  if (!msg) return null;
  if (msg.senderId) return String(msg.senderId);
  if (!msg.sender) return null;
  if (typeof msg.sender === 'string') return msg.sender;
  return msg.sender.id || msg.sender._id || null;
};

/** Shallow message shape — never attach full populated subgraphs (prevents stack overflows). */
export const normalizeMessage = (msg) => {
  if (!isPlainObject(msg)) return null;

  const rawOffer =
    isPlainObject(msg.offer) ? msg.offer : isPlainObject(msg.bid) ? msg.bid : null;
  const offer = normalizeOffer(rawOffer);
  const mappedType = MESSAGE_TYPE_MAP[msg.type] || msg.type;
  const text = msg.text ?? msg.message ?? '';

  return {
    id: String(msg.id || msg._id || ''),
    _id: String(msg._id || msg.id || ''),
    type: mappedType,
    message: text,
    text,
    createdAt: msg.createdAt,
    readAt: msg.readAt,
    senderId: getSenderId(msg),
    offer,
    bid: offer,
    bidAmount: offer ? offer.amount : undefined,
  };
};

export const normalizeMessages = (payload) => {
  if (!payload) return [];
  let list = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (isPlainObject(payload) && Array.isArray(payload.messages)) {
    list = payload.messages;
  } else if (isPlainObject(payload?.data) && Array.isArray(payload.data.messages)) {
    list = payload.data.messages;
  }
  const normalized = list.map(normalizeMessage).filter(Boolean);
  return normalized.length > 500 ? normalized.slice(-500) : normalized;
};

export const getOfferAmount = (offer) => {
  if (!offer) return 0;
  return Number(offer.amount ?? offer.bidPrice ?? offer.bidAmount ?? 0);
};

export const isProposalMessage = (msg) =>
  (msg?.type === 'BID_PROPOSAL' || msg?.type === 'OFFER_CREATED') &&
  !isSellerCounterPending(msg);

export const isBuyerMessage = (msg, buyerId) => {
  if (!buyerId) {
    return isProposalMessage(msg) || msg?.type === 'PRODUCT_INQUIRY';
  }
  const senderId = getSenderId(msg);
  if (!senderId) {
    return isProposalMessage(msg) || msg?.type === 'PRODUCT_INQUIRY';
  }
  return String(senderId) === String(buyerId);
};

export const isAcceptedMessage = (msg) =>
  msg?.type === 'BID_ACCEPTED' || msg?.type === 'OFFER_ACCEPTED';

export const isRejectedMessage = (msg) =>
  msg?.type === 'BID_REJECTED' ||
  msg?.type === 'OFFER_REJECTED' ||
  msg?.type === 'OFFER_CANCELLED' ||
  msg?.type === 'OFFER_EXPIRED';

export const isOfferMessage = (msg) =>
  isProposalMessage(msg) ||
  isCounterMessage(msg) ||
  isAcceptedMessage(msg) ||
  isRejectedMessage(msg);

export const getOfferIdFromMessage = (msg) => {
  if (!msg) return null;
  if (typeof msg.offer === 'string') return msg.offer;
  if (typeof msg.bid === 'string') return msg.bid;
  const entity = msg.offer || msg.bid;
  if (isPlainObject(entity)) {
    return entity.id || entity._id || null;
  }
  return null;
};

/** Populated API offers embed product/conversation graphs; copying them overflows the stack. */
const offerHasPopulatedGraph = (offer) =>
  isPlainObject(offer) &&
  (isPlainObject(offer.product) ||
    isPlainObject(offer.buyer) ||
    isPlainObject(offer.seller) ||
    isPlainObject(offer.conversation));

export const getOfferFromMessage = (msg) => {
  if (!msg) return null;
  if (typeof msg.offer === 'string') {
    return { id: msg.offer, _id: msg.offer, amount: 0, bidPrice: 0, bidAmount: 0 };
  }
  if (typeof msg.bid === 'string') {
    return { id: msg.bid, _id: msg.bid, amount: 0, bidPrice: 0, bidAmount: 0 };
  }
  const entity = isPlainObject(msg.offer) ? msg.offer : isPlainObject(msg.bid) ? msg.bid : null;
  if (!entity) return null;
  // Already shallow from normalizeMessage — return as-is to avoid redundant work each render.
  if (!offerHasPopulatedGraph(entity)) return entity;
  return normalizeOffer(entity);
};

export const normalizeOfferList = (list) =>
  Array.isArray(list) ? list.map(normalizeOffer).filter(Boolean) : [];

export const isOfferActiveAccepted = (offer) => {
  if (!offer) return false;
  if (offer.status === 'CONVERTED' || offer.convertedToCart) return false;
  if (offer.status !== 'ACCEPTED') return false;
  if (offer.expiresAt && new Date(offer.expiresAt) <= new Date()) return false;
  return true;
};
